export const SITE_CONTENT = {
  brand: "Appinfigo",
  promoBar: {
    enabled: true,
    items: [
      "4 products live in production — click any to open",
      "Scoping sprints open this month — ₹25k–₹50k, 1 week",
      "MVPs from ₹1.5L · Fixed price · Senior engineers only",
      "Akash takes every first call — book a 30-min consult"
    ],
    ctaLabel: "See live work",
    ctaPath: "/case-studies"
  },
  /* Single source of truth for the header navigation — the desktop bar and the
     mobile drawer both render from this list, so a page added here becomes
     reachable in both at once. Every `path` must resolve to a real route:
     "/" and "/contact" are declared in App.js, everything else must exist in
     `pages` below. Top-level entries with `children` render as a disclosure
     dropdown; the parent `path` also drives active-state matching. */
  navItems: [
    { label: "Home", path: "/" },
    {
      label: "Services",
      path: "/services",
      children: [
        { label: "All Services", path: "/services" },
        { label: "Product Engineering", path: "/product-engineering" },
        { label: "Web Development", path: "/web-development-services" },
        { label: "Mobile App Development", path: "/mobile-app-development" },
        { label: "UI/UX Design", path: "/ui-ux-design-services" },
        { label: "Data and AI", path: "/data-and-ai" },
        { label: "Cloud Modernization", path: "/cloud-modernization" },
        { label: "Cloud Services", path: "/cloud-services" },
        { label: "DevOps Services", path: "/devops-services" },
        { label: "Managed Support", path: "/managed-support" },
        { label: "Cybersecurity", path: "/cybersecurity" },
        { label: "Compliance and Risk", path: "/compliance" }
      ]
    },
    {
      label: "Industries",
      path: "/industries",
      children: [
        { label: "All Industries", path: "/industries" },
        { label: "Healthcare", path: "/healthcare-software-solutions" },
        { label: "Fintech and Banking", path: "/fintech-banking-solutions" },
        { label: "Real Estate", path: "/real-estate-digital-solutions" },
        { label: "Travel and Logistics", path: "/travel-logistics-solutions" },
        { label: "Public Sector", path: "/public-sector-it-solutions" }
      ]
    },
    {
      label: "Work",
      path: "/case-studies",
      children: [
        { label: "Case Studies", path: "/case-studies" },
        { label: "Client Reviews", path: "/client-reviews" }
      ]
    },
    {
      label: "Pricing",
      path: "/pricing",
      children: [
        { label: "Pricing", path: "/pricing" },
        { label: "Engagement Model", path: "/engagement-model" },
        { label: "FAQ", path: "/faq" }
      ]
    },
    {
      label: "Company",
      path: "/about",
      children: [
        { label: "About", path: "/about" },
        { label: "Leadership", path: "/leadership" },
        { label: "Partners", path: "/partners" },
        { label: "Careers", path: "/careers" },
        { label: "CSR", path: "/corporate-social-responsibility" },
        { label: "Insights", path: "/insights" }
      ]
    },
    { label: "Contact", path: "/contact" }
  ],
  hero: {
    eyebrow: "AI-Native Product Engineering",
    /* The giant line across the middle of the homepage hero (re-lined from
       signals below). It breaks after each comma. Keep it true: it is backed
       by the FAQ's "Most MVPs ship in 6 to 10 weeks" and statsBand's 6 wks. */
    headline: "Ship in weeks, not quarters.",
    title: "Modern software, AI built in from day one",
    subtitle:
      "We build production-ready web, mobile, and operational platforms with AI features designed in—not bolted on. Founder-led delivery, transparent pricing, and code you actually own.",
    primaryCta: "Book a 30-min consult",
    secondaryCta: "See live products",
    trustNote: "Hands-on partner for founders and product teams shipping real software in 2026.",
    signals: [
      "AI-native architecture",
      "Ship in weeks, not quarters",
      "Direct access to senior engineers"
    ],
    panel: {
      label: "How we work",
      title: "Small senior team. Modern stack. Honest delivery.",
      description: "No layered account managers, no offshore handoffs. You work directly with the engineers shipping your product.",
      steps: [
        {
          phase: "Week 1 — Scope",
          detail: "We map the build, lock the milestones, and quote a fixed price."
        },
        {
          phase: "Week 2+ — Ship",
          detail: "Working software each week with live demos and a public progress board."
        },
        {
          phase: "Post-launch — Grow",
          detail: "Stay on for AI features, performance work, and roadmap support."
        }
      ]
    }
  },
  metrics: [
    { value: "Live", label: "Products shipped in travel, logistics, and ops" },
    { value: "Senior", label: "Engineers on every project—no juniors hidden in the team" },
    { value: "Fixed", label: "Scoped pricing before a single line of code" }
  ],
  heroBadges: [
    "Registered Indian Pvt Ltd",
    "Senior engineers only",
    "AI-native delivery"
  ],
  statsBand: {
    title: "Proof, not promises",
    subtitle: "A small senior team with real products running in production today.",
    items: [
      { value: 4, suffix: "", label: "Products live in production" },
      { value: 100, suffix: "%", label: "Senior engineers on every project" },
      { value: 6, suffix: " wks", label: "Typical MVP delivery time" },
      { value: 24, suffix: "h", label: "Typical response on Slack" }
    ]
  },
  techStack: {
    title: "The stack we build on",
    subtitle: "Boring, proven technology, chosen per use case and owned by you from day one.",
    items: [
      "React",
      "Next.js",
      "Node.js",
      "Python",
      "React Native",
      "TypeScript",
      "AWS",
      "Vercel",
      "Firebase",
      "PostgreSQL",
      "OpenAI",
      "Anthropic"
    ]
  },
  industriesSection: {
    title: "Industries we build for",
    subtitle: "Domain-aware engineering across regulated and high-scale sectors."
  },
  trustBadges: {
    title: "Built to work with, not around",
    subtitle: "Registered, compliant, and contract-ready from the first kickoff.",
    items: [
      { title: "Registered Pvt Ltd", desc: "Indian Private Limited entity" },
      { title: "GST-compliant invoicing", desc: "Input tax credit for Indian clients" },
      { title: "MSA & NDA ready", desc: "Signed on first kickoff" },
      { title: "You own the IP", desc: "Code, infra and repos from day one" },
      { title: "Security by default", desc: "OWASP checks before every launch" },
      { title: "Production monitoring", desc: "Sentry, alerts and on-call runbooks" }
    ]
  },
  partnerIntro: {
    title: "A small senior team. Not a giant agency.",
    description:
      "Most software shops sell you a project manager, a layer of juniors, and a long invoice. We do the opposite—senior engineers, direct access, fixed-scope pricing, and AI features built in from day one. If you want a vendor, we are not the right fit. If you want a partner who ships, talk to us."
  },
  trustLogos: [
    "FlyJed",
    "MM Shubh Yatra",
    "Topsel Trucking",
    "Toyota Stock Track",
    "Shree Vishwshraddha",
    "Zeta"
  ],
  consultStrip: {
    title: "Have an idea you want shipped?",
    subtitle: "Akash takes every first call. 30 minutes. No sales pitch. We will tell you honestly if we can build it.",
    button: "Book a call with Akash"
  },
  servicesTitle: "Service Offerings",
  servicesSubtitle:
    "Complete spectrum of software development services, all in one place with flexible engagement models to suit your needs.",
  services: [
    {
      name: "Digital Strategy and Design",
      points: [
        "UI/UX Consulting",
        "Technology Consulting",
        "Product Discovery & Framing",
        "Experience Design"
      ]
    },
    {
      name: "Product Engineering",
      points: [
        "Custom Application Development",
        "Mobile App Development",
        "Legacy Systems Modernization",
        "DevOps & Continuous Delivery"
      ]
    },
    {
      name: "Cloud and Infrastructure",
      points: [
        "Cloud Consulting & Architecture",
        "Cloud Migration Services",
        "Managed Cloud Services",
        "Cloud Native Development"
      ]
    },
    {
      name: "Intelligent Automation",
      points: [
        "AI Consulting & Strategy",
        "Custom AI/ML Solutions",
        "Generative AI Implementation",
        "Computer Vision & IoT Solutions"
      ]
    },
    {
      name: "Application Security",
      points: [
        "Security Consulting & Assessment",
        "Penetration Testing (VAPT)",
        "DevSecOps Services",
        "Managed Security Services"
      ]
    },
    {
      name: "Business Intelligence",
      points: [
        "BI Consulting & Strategy",
        "Data Warehousing & ETL",
        "Data Governance",
        "Analytics & Reporting"
      ]
    }
  ],
  industries: [
    "Healthcare",
    "Software & Technology",
    "Fintech & Banking",
    "Insurance & Risk",
    "Real Estate",
    "Travel & Logistics",
    "Media & Entertainment",
    "Public Sector",
    "E-Commerce & Retail"
  ],
  caseStudies: {
    title: "View our work",
    subtitle: "Live products delivered by Appinfigo across travel, logistics, and enterprise operations.",
    items: [
      {
        name: "FlyJed | Premium Travel & Aviation",
        outcome: "Travel booking and aviation services platform",
        summary: "A premium travel experience platform focused on booking workflows, customer confidence, and streamlined service access.",
        website: "https://flyjed.in",
        /* Shown instead of the raw host — the deploy URL is not the brand. */
        label: "FlyJed",
        screenshot: "/images/projects/flyjed.jpg"
      },
      {
        name: "MM Shubh Yatra | Travel & Pilgrimage Services",
        outcome: "Travel and pilgrimage service booking experience",
        summary: "A travel and pilgrimage service platform with structured booking journeys, support access, and service discovery for end users.",
        website: "https://mmshubhyatra.com",
        /* Shown instead of the raw host — the deploy URL is not the brand. */
        label: "MM Shubh Yatra",
        screenshot: "/images/projects/mms-hubh-yatra.jpg"
      },
      {
        name: "Topsel Trucking | Logistics Management Portal",
        outcome: "Commercial logistics and support operations portal",
        summary: "A logistics-focused website and operations portal for commercial trucking services, location visibility, and service workflows.",
        website: "https://topseltrucking.com",
        /* Shown instead of the raw host — the deploy URL is not the brand. */
        label: "Topsel Trucking",
        screenshot: "/images/projects/topsel-trucking.jpg"
      },
      {
        name: "Toyota Stock Track | Inventory Management System",
        outcome: "Inventory visibility and stock tracking operations",
        summary: "An internal stock tracking system designed for inventory visibility, control, and faster operational decisions.",
        website: "https://topseltoyotastocktrack.netlify.app",
        /* Shown instead of the raw host — the deploy URL is not the brand. */
        label: "Topsel Toyota",
        screenshot: "/images/projects/toyota-stock-track.jpg"
      },
      {
        name: "Shree Vishwshraddha | Ayurvedic Hospital Website",
        outcome: "Patient enquiry site with WhatsApp-first booking",
        summary:
          "A bilingual hospital site for an Ayurvedic clinic in Prayagraj — treatment pages, doctor profiles, a symptom self-check, and appointment booking that opens straight into WhatsApp instead of a contact form.",
        website: "https://www.vishwshraddhahospital.com",
        /* Shown instead of the raw host — the deploy URL is not the brand. */
        label: "Vishwshraddha Hospital",
        screenshot: "/images/projects/vishwshraddha-hospital.jpg"
      }
    ]
  },
  testimonials: {
    title: "What working with us actually feels like",
    subtitle: "We will not put fake testimonials on this page. Instead, here is exactly what every engagement with Appinfigo gives you, no exceptions.",
    items: [
      {
        text: "Every Friday you see a working build deployed to staging—not slides, not a status report. Click through and use it before paying the next milestone.",
        name: "Working software every week",
        role: "Demo cadence, never skipped"
      },
      {
        text: "Akash, Avishek, Anu, and Samarjeet are reachable on Slack and WhatsApp during the build. Most messages get a real reply within an hour.",
        name: "Direct access to the team",
        role: "No account manager in between"
      },
      {
        text: "One paid week of scoping. After that, a fixed price for the full build. The number on the quote is the number on the final invoice—no hourly surprises.",
        name: "Fixed price after scoping",
        role: "Plus GST, milestone-based"
      },
      {
        text: "Your code lives in your GitHub or GitLab org from day one. Your AWS, Vercel, or Firebase account. Your domain. We are guests in your infrastructure, not gatekeepers.",
        name: "You own everything",
        role: "Repo, infra, IP, day one"
      },
      {
        text: "Public progress board you can check anytime. Tasks in flight, tasks done, tasks blocked. No waiting for a status call to know where things are.",
        name: "Transparent progress board",
        role: "Live, anytime, no permission needed"
      },
      {
        text: "Senior engineer on every line of code. No junior bench. If we cannot staff your project properly, we will tell you and not take the work.",
        name: "Senior engineers only",
        role: "Day one to launch"
      },
      {
        text: "GST invoice issued through our Indian Private Limited entity. MSA and NDA signed on first kickoff. Indian clients can claim input tax credit.",
        name: "Registered Indian Pvt Ltd",
        role: "GST-compliant, contracts-ready"
      },
      {
        text: "Monitoring, error tracking, and on-call runbooks shipped as part of the launch. You hear about production problems before your users do.",
        name: "Production-ready at launch",
        role: "Sentry, alerts, runbooks included"
      },
      {
        text: "If a milestone misses what we agreed in scoping, we fix it on our time before you pay the next invoice. Rework caused by us is on us.",
        name: "Rework on our time",
        role: "Fixed-price means fixed-quality"
      }
    ]
  },
  outcome: {
    title: "You define the outcome. We ship the product.",
    bullets: [
      {
        title: "End-to-end product ownership",
        desc: "Hand us a problem—a SaaS app, a mobile product, an internal portal—and we take it from whiteboard to production. You set the goal; we handle architecture, build, QA, and deploy."
      },
      {
        title: "AI features that actually work",
        desc: "Intelligent search, GenAI chat, document understanding, predictive workflows. We design AI into the product from the start so it ships—not as a demo, but as a feature your users actually use."
      },
      {
        title: "Honest scope, honest price",
        desc: "Every engagement starts with a one-week scoping sprint and a fixed-price quote. No hourly billing, no scope creep invoices, no surprises at month three."
      }
    ]
  },
  faq: {
    title: "What teams ask us before they sign",
    subtitle: "Straight answers on price, team, timelines, and what happens if something goes wrong.",
    items: [
      {
        question: "How much will my project cost?",
        answer: "We do a paid one-week scoping sprint, then quote a fixed price for the full build. Most products land between ₹1.5L and ₹18L (plus GST) depending on scope. AI features, mobile, and integrations affect the range. International clients can pay in USD at the equivalent rate. You see the number before you commit."
      },
      {
        question: "Who actually writes the code?",
        answer: "Senior engineers only. No layered handoffs, no junior teams hidden behind a project manager. You will know the names and Slack handles of the people building your product."
      },
      {
        question: "How fast can you ship?",
        answer: "Most MVPs ship in 6 to 10 weeks from the scoping call. Larger platforms run 3 to 6 months. We show working software each week, not status decks."
      },
      {
        question: "Do you build with AI features?",
        answer: "Yes—it is our default. Intelligent search, document Q&A, GenAI chat, predictive workflows, automation copilots. We design AI in from the architecture stage, not as a last-minute add-on."
      },
      {
        question: "What if I am not happy with the build?",
        answer: "Fixed-price means we eat the rework, not you. If a milestone misses the agreed acceptance criteria, we fix it on our time before you pay the next invoice."
      },
      {
        question: "Do I own the code?",
        answer: "Always. Code, infrastructure, repos, and IP transfer to you from day one. You can take the codebase to any other team at any point with zero friction."
      }
    ]
  },
  comparison: {
    title: "What makes Appinfigo different",
    subtitle: "A side-by-side view of how we approach delivery compared to typical vendor-led execution.",
    leftLabel: "Appinfigo",
    rightLabel: "Typical Vendor",
    rows: [
      {
        point: "Delivery model",
        appinfigo: "Outcome-based milestones",
        vendor: "Hour-based task completion"
      },
      {
        point: "Ownership",
        appinfigo: "End-to-end product accountability",
        vendor: "Execution-only handoff"
      },
      {
        point: "Transparency",
        appinfigo: "Weekly demos with KPI tracking",
        vendor: "Periodic status updates"
      },
      {
        point: "Architecture quality",
        appinfigo: "Scalable and security-first",
        vendor: "Depends on project constraints"
      },
      {
        point: "Post-launch support",
        appinfigo: "Roadmap and optimization partnership",
        vendor: "Limited maintenance scope"
      }
    ]
  },
  engagement: {
    title: "Flexible engagement models for every stage",
    subtitle: "Choose a structure that matches your delivery velocity, internal bandwidth, and product ownership expectations.",
    items: [
      {
        name: "Dedicated Engineering Pod",
        bestFor: "Scaleups and enterprise product teams",
        points: [
          "Cross-functional squad with product, design, and engineering",
          "Weekly planning, demos, and KPI-aligned releases",
          "Long-term roadmap execution with continuity"
        ]
      },
      {
        name: "Project-Based Delivery",
        bestFor: "Defined scope and fixed timeline initiatives",
        points: [
          "Discovery, architecture, build, QA, and deployment",
          "Milestone-driven execution with transparent reporting",
          "Controlled delivery with clear acceptance criteria"
        ]
      },
      {
        name: "Team Augmentation",
        bestFor: "Organizations expanding internal capacity",
        points: [
          "Specialized engineers integrated into your workflows",
          "Fast onboarding with clear ownership boundaries",
          "Elastic scaling based on sprint priorities"
        ]
      }
    ]
  },
  whyUs: {
    title: "Why teams choose Appinfigo",
    subtitle: "We are a small, senior team. That is the feature, not the limitation.",
    stats: [
      { value: "Senior", label: "Engineers on every line of your code" },
      { value: "Fixed", label: "Scope and price agreed before kickoff" },
      { value: "Direct", label: "Slack with the builders, not account managers" }
    ],
    points: [
      "Fixed-scope pricing—no hourly billing surprises",
      "AI features designed into the architecture, not bolted on later",
      "You own the code, the infra, and the IP from day one"
    ]
  },
  locations: {
    title: "Where we work from",
    subtitle: "Headquartered in Prayagraj, India. We work remotely with clients across India, the US, and the UK.",
    offices: ["Prayagraj, India"]
  },
  finalCta: {
    title: "Have a product to build?",
    subtitle:
      "Book a 30-minute call with Akash. We will tell you honestly if we are the right team, and a fair price if we are.",
    description: "After the call you will have: a real assessment of your idea, an architecture recommendation, and a fixed-price quote if we move forward.",
    button: "Book a call with Akash"
  },
  footer: {
    email: "info@appinfigo.com",
    phone: "+91 7348370221",
    locations: ["Prayagraj, India",],
    serviceLinks: [
      { label: "Product Engineering", path: "/product-engineering" },
      { label: "Data and AI", path: "/data-and-ai" },
      { label: "Web Development", path: "/web-development-services" },
      { label: "Mobile Apps", path: "/mobile-app-development" },
      { label: "Cloud and DevOps", path: "/cloud-modernization" },
      { label: "Compliance and Risk", path: "/compliance" }
    ],
    companyLinks: [
      { label: "About Us", path: "/about" },
      { label: "Case Studies", path: "/case-studies" },
      { label: "Pricing", path: "/pricing" },
      { label: "FAQ", path: "/faq" },
      { label: "Contact", path: "/contact" }
    ],
    supportLinks: [
      { label: "Travel and Logistics", path: "/travel-logistics-solutions" },
      { label: "Fintech Solutions", path: "/fintech-banking-solutions" },
      { label: "Healthcare Software", path: "/healthcare-software-solutions" },
      { label: "Privacy Policy", path: "/privacy-policy" },
      { label: "Terms", path: "/terms" }
    ]
  },
  pages: [
    {
      path: "/services",
      eyebrow: "What we build",
      title: "AI-native software, built end-to-end",
      subtitle: "We design, build, and ship complete products. Fixed scope. Fixed price. Senior engineers on every line of code.",
      sections: [
        {
          heading: "Web product engineering",
          body: "SaaS apps, internal tools, admin portals, customer-facing platforms. Modern stack (React, Next.js, Node, Python). From whiteboard to production in 6 to 12 weeks for most builds."
        },
        {
          heading: "Mobile apps (iOS and Android)",
          body: "Cross-platform mobile apps in React Native or native when needed. Real product flows—not template work. Includes app store submission and post-launch crash monitoring."
        },
        {
          heading: "AI features and copilots",
          body: "Intelligent search, document Q&A, GenAI chat, predictive workflows, voice agents, automation copilots. We design AI into the architecture from day one so it ships."
        },
        {
          heading: "Operational platforms and portals",
          body: "Booking systems, dispatch and logistics portals, inventory and stock tracking, internal admin tools. Our deepest expertise—we have shipped four of these in production already."
        },
        {
          heading: "Cloud, DevOps, and reliability",
          body: "AWS, GCP, Vercel, Firebase. CI/CD pipelines, infrastructure as code, monitoring, alerting, and on-call runbooks. Built so you can run the product without us if you choose to."
        },
        {
          heading: "Legacy modernization",
          body: "Take an old PHP or jQuery app and rebuild it as a modern web product without breaking what already works. We migrate users, data, and integrations carefully and incrementally."
        }
      ],
      pageFaqs: [
        {
          question: "Do you do design too, or just engineering?",
          answer: "We do product UX and UI design as part of every build. For brand-heavy work we partner with a designer you trust or one we recommend."
        },
        {
          question: "What stacks do you work in?",
          answer: "React/Next.js + Node/Python for web, React Native for mobile, Firebase/Supabase or Postgres for data, AWS/Vercel for infra. We pick the boring, proven stack unless you have a specific reason for something else."
        }
      ]
    },
    {
      path: "/industries",
      eyebrow: "Industry Focus",
      title: "Domain-led engineering across complex industries",
      subtitle: "We align architecture, delivery controls, and product priorities to the regulatory and operational reality of each sector.",
      sections: [
        {
          heading: "Fintech and Banking",
          body: "Secure product development with compliance-aware architecture and fraud-ready workflows."
        },
        {
          heading: "Healthcare and Life Sciences",
          body: "Reliable systems for patient experience, operations, and sensitive data handling."
        },
        {
          heading: "Insurance and Risk",
          body: "Claims, underwriting, and policy lifecycle platforms designed for scale, traceability, and customer experience."
        },
        {
          heading: "Software and Technology",
          body: "SaaS engineering, platform evolution, and architecture modernization for product-led companies."
        },
        {
          heading: "Retail, E-Commerce, and Consumer",
          body: "Omnichannel commerce systems, fulfillment integrations, and conversion-focused product improvements."
        },
        {
          heading: "Public Sector and Enterprise Operations",
          body: "Resilient digital platforms with governance, accessibility, and process continuity at scale."
        }
      ]
    },
    {
      path: "/case-studies",
      eyebrow: "Our work",
      title: "Live products we built. Live links you can open.",
      subtitle: "Every project below is in production with real users. Click through and see for yourself.",
      sections: [
        {
          heading: "FlyJed — Premium Travel and Aviation",
          body: "A travel and aviation booking platform with a polished booking flow, customer login, and operator dashboards. We built the full web product, payment integration, and admin tooling. Live at flyjed.in."
        },
        {
          heading: "MM Shubh Yatra — Travel and Pilgrimage",
          body: "A pilgrimage and travel services platform with package discovery, structured booking journeys, and a support flow tuned for first-time online users. End-to-end product engineering and content tooling. Live at mmshubhyatra.com."
        },
        {
          heading: "Topsel Trucking — Logistics Portal",
          body: "A logistics-focused website and operations portal for commercial trucking. Service discovery on the public site plus an internal portal for fleet and dispatch workflows. Live at topseltrucking.com."
        },
        {
          heading: "Toyota Stock Track — Inventory System",
          body: "An internal dealership stock tracking system covering inventory visibility, allocation, and stock movement controls. Built for daily operational use by dealership staff. Live deployment at topseltoyotastocktrack.netlify.app."
        }
      ],
      pageFaqs: [
        {
          question: "Can I talk to a past client before we sign?",
          answer: "Yes. After the discovery call, we will introduce you to one of the clients above for a 15-minute reference chat. Direct call, no script, no sales role."
        },
        {
          question: "Do you have case studies in my industry?",
          answer: "We have deep work in travel, logistics, and operational portals. For other industries (fintech, healthcare, SaaS) we can show comparable architectural work in the discovery call."
        }
      ]
    },
    {
      path: "/about",
      eyebrow: "About us",
      title: "We build AI-native software. Honestly.",
      subtitle: "A small senior team out of Prayagraj, India, founded by Akash Mishra. Code you own. Fixed prices. No surprises.",
      intro: [
        "Appinfigo was started by Akash Mishra because most software shops sell the same thing: a project manager on the call, a layer of juniors in the build, and an invoice that grows every month. We wanted to do the opposite.",
        "We are a small senior team that builds web, mobile, and AI-powered products end-to-end. Every project is scoped and priced before kickoff. The four people on the team are the four people building your product—Akash on the founder and architecture side, Avishek running delivery, Anu owning product and design, Samarjeet handling business and partnerships. That is it. No layered handoffs, no offshore mystery, no surprise invoices."
      ],
      sections: [
        {
          heading: "We are senior, on purpose",
          body: "Every person on every project is a senior engineer. No juniors hidden in the team. No 'resource allocation' from a bench. If we cannot staff your project with the right people, we will tell you and not take the work."
        },
        {
          heading: "We charge for the build, not the hours",
          body: "We do a paid one-week scoping sprint. After that you get a fixed-price quote for the entire build. If a milestone misses what we agreed, we fix it on our time. You only ever pay the price we agreed to."
        },
        {
          heading: "We build AI in, not on top",
          body: "Most teams bolt AI onto an existing product as a chatbot. We design AI into the architecture from day one so it ships as a feature your users actually use—intelligent search, document Q&A, predictive workflows, automation copilots."
        },
        {
          heading: "You own everything",
          body: "Source code, infrastructure, repos, IP—yours from day one. You can take the codebase to any other team at any point and we will help with the handover."
        },
        {
          heading: "We work in the open",
          body: "Weekly working demos, not status decks. A public progress board you can check anytime. Direct Slack with the engineers building your product. If something is slipping, you will hear it from us first."
        }
      ],
      differentiators: [
        {
          title: "Fixed scope, fixed price",
          body: "Every engagement starts with a one-week scoping sprint and ends with a fixed-price quote. No hourly billing, no scope creep invoices, no surprises at month three."
        },
        {
          title: "Senior-only team",
          body: "No juniors hidden in the team. You work directly with the engineers writing your code. Real names, real Slack handles, real availability."
        },
        {
          title: "AI-native by default",
          body: "We build AI features into the architecture instead of bolting them on. Search that understands intent, automation that actually saves work, copilots that ship."
        }
      ],
      process: [
        {
          title: "Week 0 — Free 30-min call",
          body: "Tell us what you want to build. We will tell you honestly if we are the right team and roughly what it will cost."
        },
        {
          title: "Week 1 — Paid scoping sprint",
          body: "We spend a week mapping the architecture, breaking down milestones, and writing a fixed-price quote for the full build. If you do not move forward, you keep the doc."
        },
        {
          title: "Weeks 2 to N — Build",
          body: "Working software every week. Live demos, public progress board, direct Slack with the engineers. We ship continuously, not at the end."
        },
        {
          title: "Post-launch — Grow",
          body: "Optional retainer for performance, new features, AI additions, and on-call support. Or take the code and run with another team. Your choice."
        }
      ],
      pageFaqs: [
        {
          question: "How small is small?",
          answer: "Four people on the leadership team. Akash on every project call. Big enough to ship production software in 6–10 weeks. If we cannot do your project well, we will tell you and refer you elsewhere."
        },
        {
          question: "Why fixed-price instead of hourly?",
          answer: "Hourly billing rewards us for moving slow. Fixed-price rewards us for shipping. We would rather earn your repeat business than your hour 41."
        },
        {
          question: "Are you remote? Do you travel?",
          answer: "We are based in Prayagraj, India and work remote-first with clients in India, the US, and the UK. For larger engagements we will come on-site for kickoff if it helps."
        },
        {
          question: "Are you a registered company?",
          answer: "Yes. Appinfigo is a registered Indian Private Limited company. We issue GST-compliant invoices and can sign standard MSA and NDA documents on first kickoff."
        }
      ]
    },
    {
      path: "/leadership",
      eyebrow: "Who you work with",
      title: "Founder-led. Senior team. No layers.",
      subtitle: "The four people below are the ones building your product. No sales reps in between, no offshore mystery, no account managers translating between you and the team.",
      intro: [
        "Most software firms put a salesperson on the call, a project manager in your inbox, and the actual engineers somewhere you cannot see. We do not. The founder is on every first call, on every weekly demo, and reachable on Slack throughout the build.",
        "Our team is small on purpose. Every person here is senior in what they do. Every architecture decision is made by someone who has shipped that thing before."
      ],
      team: [
        {
          name: "Akash Mishra",
          role: "Founder and CEO",
          bio: "On every project from first call to final launch. Sets the architecture, owns the scoping sprint, and is the direct escalation point if anything goes sideways. If you sign with Appinfigo, you are signing with Akash."
        },
        {
          name: "Avishek",
          role: "Operations and Delivery Lead",
          bio: "Runs weekly delivery, keeps milestones on track, and makes sure every build ships on the timeline you were quoted. Your day-to-day point of contact once kickoff is done."
        },
        {
          name: "Anu Shukla",
          role: "Product and Design Lead",
          bio: "Turns your idea into a real product flow. Owns UX decisions, the design system, and the user-facing experience of every build we ship. Has shipped travel, logistics, and operational portals."
        },
        {
          name: "Samarjeet Ranjan Bala",
          role: "Business and Partnerships",
          bio: "Your first conversation. Handles scoping discussions, pricing, and partnership questions. Knows the team capacity well enough to tell you honestly if Appinfigo is the right fit for your project."
        }
      ],
      sections: [
        {
          heading: "Direct access to the people building your product",
          body: "You will have Slack access to the engineers writing your code. Real availability, not a ticket queue. Weekly working demos with the actual team, not a status meeting with a project manager."
        },
        {
          heading: "Founder on every project",
          body: "Every engagement has the founder involved through scoping, architecture, and weekly checkpoints. If you escalate something, you escalate it to the person who can fix it—not a layer above them."
        },
        {
          heading: "Senior engineers only",
          body: "We do not have a junior bench. Every line of code is written or reviewed by a senior engineer with shipped production experience in the relevant stack."
        }
      ],
      serviceHighlights: [
        {
          heading: "Weekly demos",
          body: "Every Friday you see working software. Not slides, not status reports—an actual deployed build you can click through."
        },
        {
          heading: "Public progress board",
          body: "A live progress board you can check anytime. Tasks in flight, tasks done, tasks blocked. No need to wait for a status call."
        },
        {
          heading: "One-business-day response",
          body: "Slack messages get a real response from a real engineer within one business day. Most within the same hour."
        }
      ],
      pageFaqs: [
        {
          question: "What happens if my project's main engineer leaves?",
          answer: "We pair engineers on every project so there is always a second person who knows the codebase. The founder is also active enough on every project to bridge any gap. We have not had a project disrupted by a team change."
        },
        {
          question: "Who do I escalate to if something is going wrong?",
          answer: "The founder. There is no layer above. If a project is slipping, you will hear it from us before you have to ask."
        }
      ]
    },
    {
      path: "/contact",
      eyebrow: "Contact",
      title: "Talk to our product and engineering specialists",
      subtitle: "Share your goals and we will map a practical execution approach with timelines and milestones.",
      sections: [
        {
          heading: "Consultation Setup",
          body: "Book a discovery conversation tailored to your product stage and priorities."
        },
        {
          heading: "Solution Planning",
          body: "Get architecture direction, team model suggestions, and delivery roadmap options."
        },
        {
          heading: "Delivery Kickoff",
          body: "Move from validated scope to execution with a clear team and sprint plan."
        }
      ]
    },
    {
      path: "/product-engineering",
      eyebrow: "What we build",
      title: "Product engineering, end to end",
      subtitle: "We take you from whiteboard sketch to a deployed, production-ready product. One team. Fixed scope. Fixed price.",
      sections: [
        {
          heading: "Week 0 — Discovery call",
          body: "Free 30-minute call. You explain what you want to build, we tell you honestly whether we are the right team. No deck, no script."
        },
        {
          heading: "Week 1 — Paid scoping sprint",
          body: "One week of senior-engineer time. We map the architecture, break the build into milestones, and write a fixed-price quote for the full project. The doc is yours either way."
        },
        {
          heading: "Build — Weekly working software",
          body: "Every Friday you see a working build, not slides. You have direct Slack access to the engineers shipping the code. A public progress board shows what is done, what is in flight, and what is blocked."
        },
        {
          heading: "Launch — Production-ready",
          body: "We deploy to your infrastructure (AWS, GCP, Vercel, Firebase—your call). Monitoring, alerts, error tracking, and on-call runbooks are part of the build, not an add-on."
        },
        {
          heading: "Post-launch — Optional retainer",
          body: "Stay on for performance work, new features, AI additions, or on-call support. Or take the code and run with another team. Your codebase, your call."
        }
      ],
      pageFaqs: [
        {
          question: "What does a typical product engineering engagement cost?",
          answer: "Most MVPs land between ₹1.5L and ₹18L (plus GST) for the full build. We give you a fixed price after the one-week scoping sprint. No hourly surprises after that. International clients pay equivalent USD."
        },
        {
          question: "Can I see the code as you build?",
          answer: "Yes. Code lives in your GitHub or GitLab org from day one. You can read, review, and audit every commit."
        }
      ]
    },
    {
      path: "/cloud-modernization",
      eyebrow: "Service",
      title: "Cloud Modernization",
      subtitle: "Move your stack to AWS, GCP, or Vercel. Set up the CI/CD pipeline that actually deploys. Pay less for hosting than you do now.",
      sections: [
        {
          heading: "Move to modern cloud (AWS / GCP / Vercel)",
          body: "Migrate from old shared hosting, on-prem servers, or a single VPS to a real cloud setup with auto-scaling, backups, and a deploy pipeline that does not require SSH on a Friday night."
        },
        {
          heading: "CI/CD that actually deploys",
          body: "GitHub Actions or GitLab CI pipelines that build, test, and deploy on every push. No more uploading zip files. Includes preview deploys for every PR."
        },
        {
          heading: "Infrastructure as code",
          body: "Terraform or Pulumi so your infra is in a repo, reviewable, and reproducible. No more 'we think someone clicked something in the console six months ago.'"
        },
        {
          heading: "Monitoring and alerts",
          body: "Sentry for errors, log aggregation, uptime monitoring, and on-call alerts. You hear about problems before your users do."
        },
        {
          heading: "Cloud cost cleanup",
          body: "Most teams overspend 30-60% on cloud. We audit your bill, kill the orphaned resources, right-size what is running, and move what should be serverless to serverless."
        }
      ],
      pageFaqs: [
        {
          question: "What does cloud modernization cost?",
          answer: "Audit + migration plan + execution typically lands between ₹80,000 and ₹3.5L (plus GST) for most small/mid setups. Larger environments scale up. Fixed quote after a 1-week audit."
        },
        {
          question: "Will my product have downtime during the migration?",
          answer: "No, if we do it right. We use parallel environments and DNS cutover so the switch is seconds long. Database migrations need a brief read-only window we agree on in advance."
        }
      ]
    },
    {
      path: "/data-and-ai",
      eyebrow: "What we build",
      title: "AI features that ship and actually get used",
      subtitle: "We build the AI features your users notice—intelligent search, document Q&A, GenAI copilots, automation flows. Practical work, not demos.",
      intro: [
        "Most AI projects fail in production because they were designed as a demo first and a product second. We build them the other way around: feature the user actually opens, architecture that handles the bad days, and a roadmap for retraining when the world changes.",
        "We use OpenAI, Anthropic, Google, and open-source models depending on what fits. We have strong opinions about which model to use for what, and where a simple search index beats a fancy LLM."
      ],
      sections: [
        {
          heading: "Intelligent search and retrieval",
          body: "Search that understands intent and synonyms instead of matching keywords. Pull the right document, the right paragraph, the right answer—not 10 blue links."
        },
        {
          heading: "Document AI and structured extraction",
          body: "Pull structured fields out of invoices, statements, KYC documents, prescriptions, contracts, and lab reports. Save staff hours every day."
        },
        {
          heading: "GenAI copilots and chat",
          body: "Embedded chat assistants for customers and internal staff. Connected to your real data and your real workflows, not a generic chatbot bolted on a corner of the page."
        },
        {
          heading: "Automation and predictive flows",
          body: "Workflow automation, anomaly detection, demand forecasting, and decision support models built into the product itself."
        },
        {
          heading: "Voice agents and call automation",
          body: "Voice-based customer support, appointment booking, and outbound calling agents built on modern LLM-backed stacks."
        }
      ],
      pageFaqs: [
        {
          question: "Which AI providers do you work with?",
          answer: "OpenAI (GPT-4, GPT-4o, o-series), Anthropic Claude, Google Gemini, and open-source models via Ollama or vLLM when self-hosting matters. We pick per use case and tell you the trade-offs."
        },
        {
          question: "Will my data be used to train someone's model?",
          answer: "No—we use provider APIs in zero-training mode (OpenAI/Anthropic both support this) or self-host. Data residency and retention are part of the scoping conversation."
        },
        {
          question: "How much does an AI feature cost?",
          answer: "Most AI features land between ₹1L and ₹5L (plus GST) for a v1 launch (search, document AI, embedded copilot). Voice agents and complex workflow automation run higher. Fixed quote after scoping."
        }
      ]
    },
    {
      path: "/managed-support",
      eyebrow: "After launch",
      title: "Monthly retainer for products in production",
      subtitle: "Keep us on for new features, performance work, on-call support, or roadmap acceleration. Predictable monthly cost. Cancel with 30 days notice.",
      sections: [
        {
          heading: "Feature work",
          body: "A steady stream of small to medium features shipped each month, prioritized by you. Most retainers fit 2-6 new features per month depending on size."
        },
        {
          heading: "Performance and reliability",
          body: "Page speed tuning, query optimization, scaling work, and the cleanup that nobody had time for during the original build."
        },
        {
          heading: "On-call and incident response",
          body: "If your product is in production and something breaks, we triage and fix it. Response time depends on the tier you pick during scoping."
        },
        {
          heading: "Security and dependency updates",
          body: "Patch updates, vulnerability fixes, library upgrades, and the boring maintenance work that keeps your product from breaking 18 months from now."
        }
      ],
      pageFaqs: [
        {
          question: "How is the retainer priced?",
          answer: "Monthly fee based on team size and response time tier. Most retainers land between ₹50,000 and ₹2.5L per month (plus GST). Cancel with 30 days notice."
        },
        {
          question: "Do you only support products you built?",
          answer: "No—we take over existing products too. We start with a 1-week code audit and then quote the retainer based on what we find."
        }
      ]
    },
    {
      path: "/cybersecurity",
      eyebrow: "Security",
      title: "Security basics, done right",
      subtitle: "We are not a dedicated security firm. We build secure software from day one and run audits when you need them. For deep penetration testing, we work with trusted security partners.",
      sections: [
        {
          heading: "Security by default in every build",
          body: "Role-based access, encryption at rest and in transit, secret management, audit logging, rate limiting, and input validation are part of every product we ship. Not a separate phase."
        },
        {
          heading: "Pre-launch security audit",
          body: "Before any product goes to production we run a security checklist—OWASP top 10, dependency scan, secrets check, auth review. A real human looks at the code."
        },
        {
          heading: "Compliance support",
          body: "We build to your compliance team's standards (HIPAA, PCI DSS, SOC 2, ISO 27001 controls). We are not the certifying body—your auditor is—but we make sure the software passes."
        },
        {
          heading: "Partner network for deep pentests",
          body: "For full penetration testing engagements, we coordinate with security-specialist firms we trust. Honest answer: we are good at building secure, not at being a security audit firm."
        }
      ],
      pageFaqs: [
        {
          question: "Do you do penetration testing?",
          answer: "We do pre-launch security review and OWASP checks. For formal pentest engagements, we coordinate with security-specialist firms we have worked with before. Honest scope—we will not over-promise on what we do not specialize in."
        },
        {
          question: "Can you help us pass a SOC 2 audit?",
          answer: "Yes—we build the technical controls (audit logs, access controls, encryption, change management) that SOC 2 requires. The audit itself is run by your chosen audit firm."
        }
      ]
    },
    {
      /* Preserved from the homepage ComplianceAccordion section so the copy stays
         crawlable once that band leaves the homepage composition. */
      path: "/compliance",
      eyebrow: "Security",
      title: "Building with compliance and risk in mind",
      subtitle: "We weave compliance into every layer of the engineering process, so your product can navigate complex regulation without trading away security or performance.",
      intro: [
        "Compliance is not a phase we bolt on before launch. Consent, data residency, access control, and audit trails are architecture decisions, and we make them in week one alongside the rest of the design.",
        "An honest boundary first: we are engineers, not auditors, and we are not a certifying body. Your compliance team or auditor defines the obligation. We build, document, and evidence the software controls that satisfy it."
      ],
      sections: [
        {
          heading: "Data privacy and protection",
          body: "Personal data is designed for, not patched around. We build consent capture and withdrawal flows, keep data residency explicit in the architecture rather than incidental to the hosting choice, and encrypt at rest and in transit by default."
        },
        {
          heading: "Security and risk management",
          body: "Threat modelling before the first sprint, role-based access controls enforced in the data layer rather than the UI, and a written incident response runbook handed over at launch. Risk decisions are recorded, so you can show why a control exists."
        },
        {
          heading: "AI and technology-specific standards",
          body: "AI features carry their own obligations. We keep model governance explicit—which model, which data, which prompt—monitor outputs for bias and drift where the use case warrants it, and write audit trails so an AI-assisted decision can be reconstructed later."
        },
        {
          heading: "Industry-specific requirements",
          body: "Every sector carries its own control set. We map your industry's requirements to concrete engineering baselines, then capture the evidence—logs, configuration, change records—your auditor will ask for."
        },
        {
          heading: "Global and regulatory frameworks",
          body: "Cross-border products face more than one rulebook. We align data flows and policy handling with the jurisdictions you actually operate in, and track regulatory change against the parts of the codebase it touches."
        },
        {
          heading: "Compliance for cloud and SaaS",
          body: "The cloud moves the boundary, it does not remove it. We document the shared responsibility split with your provider, run continuous dependency and configuration scans in CI, and review third-party vendors before they get access to production data."
        }
      ],
      pageFaqs: [
        {
          question: "Can you certify us for GDPR, HIPAA, or SOC 2?",
          answer: "No. Certification is issued by your auditor or certifying body, never by your development partner. What we do is build the technical controls—access control, encryption, audit logging, change management—and hand over the evidence your auditor asks for."
        },
        {
          question: "How early should compliance enter the build?",
          answer: "At scoping. Retrofitting consent flows, data residency, or audit trails after launch costs several times what designing them in costs. We raise the compliance questions in the first week, before the architecture is locked."
        },
        {
          question: "Who owns the compliance documentation you produce?",
          answer: "You do, along with the code, infrastructure, and repositories. Threat models, runbooks, and control notes are handed over with the build."
        }
      ]
    },
    {
      path: "/careers",
      eyebrow: "Company",
      title: "Careers at Appinfigo",
      subtitle: "Join teams building meaningful software for global products and complex industries.",
      intro: [
        "We hire engineers, designers, and delivery professionals who value ownership, clarity, and practical problem solving. Our teams work on products that operate at real scale and in regulated, high-impact environments.",
        "Career growth is structured through capability ladders, mentorship, and cross-domain project exposure, helping people build both depth and leadership readiness."
      ],
      sections: [
        {
          heading: "Engineering Culture",
          body: "We value ownership, clarity, and outcome-focused execution where engineers are trusted to solve complex problems with practical decision making."
        },
        {
          heading: "Career Growth Framework",
          body: "Structured mentorship, capability tracks, and project rotation across domains help team members build depth and leadership readiness."
        },
        {
          heading: "Learning and Certification Support",
          body: "Role-based learning paths, architecture sessions, and certification sponsorship support continuous professional development."
        },
        {
          heading: "Inclusive Work Environment",
          body: "People-first policies, transparent feedback culture, and collaborative delivery practices create a respectful and high-accountability workplace."
        },
        {
          heading: "Open Roles",
          body: "Current opportunities span product engineering, cloud platforms, data and AI, quality engineering, DevOps, and cybersecurity practices."
        }
      ],
      serviceHighlights: [
        {
          heading: "Structured learning pathways",
          body: "Role-based learning plans support progression from foundational execution to architecture and delivery leadership tracks."
        },
        {
          heading: "Mentored growth model",
          body: "Regular coaching sessions and practical feedback loops help team members improve technical and communication effectiveness."
        },
        {
          heading: "Cross-domain project mobility",
          body: "Team members gain hands-on exposure across fintech, healthcare, enterprise SaaS, and cloud-native programs."
        }
      ],
      pageFaqs: [
        {
          question: "Do you support remote and hybrid collaboration?",
          answer: "Yes. We support flexible delivery-friendly work patterns while maintaining accountability through clear operating rituals."
        },
        {
          question: "How is performance and growth evaluated?",
          answer: "Evaluation includes delivery outcomes, technical quality, ownership behavior, collaboration quality, and continuous learning progress."
        }
      ]
    },
    {
      path: "/corporate-social-responsibility",
      eyebrow: "Company",
      title: "Corporate social responsibility",
      subtitle: "We invest in people, education, and community programs while building an inclusive and responsible engineering culture.",
      sections: [
        {
          heading: "Education and Skill Development",
          body: "We support technical education through mentoring circles, internship pathways, and skill workshops for engineering, cloud, and QA careers."
        },
        {
          heading: "Community Impact",
          body: "Our teams contribute to digital literacy drives, school technology initiatives, and nonprofit support through structured volunteering."
        },
        {
          heading: "Responsible Operations",
          body: "Operational policies prioritize ethics, data privacy, inclusion, and sustainable practices across procurement and delivery operations."
        },
        {
          heading: "Inclusion and Wellbeing",
          body: "People initiatives focus on equal opportunity hiring, safe workplace practices, and leadership growth for underrepresented groups."
        },
        {
          heading: "Governance and Accountability",
          body: "CSR goals are reviewed periodically with leadership oversight, transparent reporting, and measurable participation outcomes."
        }
      ]
    },
    {
      path: "/insights",
      eyebrow: "Knowledge",
      title: "Insights and practical engineering guidance",
      subtitle: "Perspective on product delivery, architecture decisions, and scaling lessons.",
      sections: [
        {
          heading: "Product Strategy Insights",
          body: "Frameworks for prioritization, roadmap sequencing, and build-versus-buy decisions that affect product velocity and business impact."
        },
        {
          heading: "Engineering Delivery Practices",
          body: "Real delivery patterns covering architecture governance, quality controls, release confidence, and collaborative execution across distributed teams."
        },
        {
          heading: "Cloud, Data, and AI Perspectives",
          body: "Actionable analysis of modernization strategies, AI adoption trade-offs, and platform reliability models relevant to enterprise product teams."
        },
        {
          heading: "Security and Compliance Guidance",
          body: "Practical recommendations for integrating security-by-design and compliance readiness into delivery without slowing business momentum."
        },
        {
          heading: "Case-Based Learning",
          body: "Lessons distilled from real delivery programs to help teams avoid common architecture and execution pitfalls."
        }
      ]
    },
    {
      path: "/partners",
      eyebrow: "Ecosystem",
      title: "Partnerships for stronger delivery outcomes",
      subtitle: "We collaborate with technology and delivery partners to expand capability and speed.",
      intro: [
        "Partnerships are designed to increase delivery coverage without fragmenting accountability. Our ecosystem combines cloud providers, specialized advisory teams, and implementation collaborators under shared quality and governance standards.",
        "Clients receive one integrated execution model with clear ownership, coordinated communication, and consistent technical expectations across all partner touchpoints."
      ],
      sections: [
        {
          heading: "Technology Partners",
          body: "We collaborate with leading cloud, analytics, and DevOps ecosystems to accelerate architecture decisions and reduce implementation risk."
        },
        {
          heading: "Delivery Partners",
          body: "Specialized implementation and advisory partners extend domain coverage for integrations, compliance, and transformation programs."
        },
        {
          heading: "Co-innovation",
          body: "Joint accelerators and reference solutions improve time-to-market for recurring use cases in regulated and high-scale products."
        },
        {
          heading: "Partnership Framework",
          body: "Our partner model defines roles, shared quality controls, and integration workflows so client teams get one coherent delivery experience."
        },
        {
          heading: "Partner Governance and Quality",
          body: "Shared governance standards, architecture checkpoints, and unified delivery rituals ensure partner collaborations maintain consistent quality and accountability."
        }
      ],
      process: [
        {
          title: "Partner fit and capability mapping",
          body: "Select partner combinations based on technical scope, domain requirements, and program risk profile."
        },
        {
          title: "Role and responsibility design",
          body: "Define ownership boundaries, communication pathways, and decision protocols across all participating teams."
        },
        {
          title: "Unified delivery operations",
          body: "Run shared sprint cycles, architecture reviews, and quality controls under one common governance rhythm."
        },
        {
          title: "Performance and value review",
          body: "Measure partner contribution through delivery speed, quality outcomes, and business-value indicators over time."
        }
      ],
      pageFaqs: [
        {
          question: "How do you avoid partner coordination issues?",
          answer: "We use explicit RACI definitions, integrated planning, and single-view reporting to prevent siloed execution and delayed decision making."
        },
        {
          question: "Can we use your partners with our internal teams?",
          answer: "Yes. Partner models can be integrated with internal product and engineering teams through hybrid operating structures."
        }
      ]
    },
    {
      path: "/faq",
      eyebrow: "Support",
      title: "Frequently asked questions",
      subtitle: "Answers to common questions around engagement, delivery, and support models.",
      sections: [
        {
          heading: "How quickly can a project start?",
          body: "Most teams are onboarded within 1-2 weeks after discovery sign-off, with clear sprint goals, ownership boundaries, and communication cadence from day one."
        },
        {
          heading: "Can we work with your existing team?",
          body: "Yes. We support dedicated pods, augmentation, and hybrid operating models with shared tools, architecture reviews, and transparent delivery tracking."
        },
        {
          heading: "How do you manage quality and security?",
          body: "Our SDLC includes coding standards, automated testing, vulnerability checks, release gates, and role-specific accountability for quality outcomes."
        },
        {
          heading: "What reporting do we get?",
          body: "Weekly sprint demos, status dashboards, and milestone reviews provide visibility into velocity, risk, scope changes, and release readiness."
        },
        {
          heading: "Do you provide post-launch support?",
          body: "Yes. We provide managed support, optimization roadmaps, incident response processes, and SLA-based operational continuity services."
        },
        {
          heading: "How are commercial models structured?",
          body: "Commercial options are aligned to delivery goals, scope clarity, team structure, and continuity requirements so financial planning remains predictable."
        },
        {
          heading: "Can you support compliance-heavy industries?",
          body: "Yes. Teams adapt delivery controls, security standards, and documentation practices to sector-specific compliance obligations and audit expectations."
        }
      ]
    },
    {
      path: "/client-reviews",
      eyebrow: "Support",
      title: "Client reviews and delivery feedback",
      subtitle: "A snapshot of what partners value most about our collaboration model, technical quality, and execution reliability.",
      intro: [
        "Client feedback is a key signal in how we improve operating practices, communication quality, and delivery reliability. Beyond testimonials, we track repeat engagement, governance stability, and product performance outcomes after go-live.",
        "The recurring themes in reviews reflect practical strengths: transparent reporting, accountable ownership, clean architecture, and predictable release execution."
      ],
      sections: [
        {
          heading: "Product Leadership Feedback",
          body: "Clients appreciate our ability to challenge assumptions early, clarify scope quickly, and convert goals into execution-ready backlogs."
        },
        {
          heading: "Engineering Team Feedback",
          body: "Engineering leaders consistently mention clean architecture practices, code quality discipline, and improved release confidence."
        },
        {
          heading: "Program Governance Feedback",
          body: "Program managers value predictable milestones, transparent reporting, and proactive escalation on dependency and delivery risks."
        },
        {
          heading: "Support and Continuity Feedback",
          body: "Operations teams report smoother handovers, improved production stability, and reliable response during critical release windows."
        },
        {
          heading: "Long-term Partnership Feedback",
          body: "Across multi-year engagements, clients highlight consistency, responsiveness, and strategic thinking beyond day-to-day development tasks."
        },
        {
          heading: "Outcome Visibility Feedback",
          body: "Business sponsors value clarity on delivery KPIs, roadmap progress, and dependency management through structured weekly and monthly reviews."
        },
        {
          heading: "Trust and Communication Feedback",
          body: "Client teams repeatedly cite proactive communication, transparent escalation handling, and dependable execution ownership as key reasons for renewal."
        }
      ],
      serviceHighlights: [
        {
          heading: "High renewal confidence",
          body: "Long-duration engagements indicate trust in delivery consistency, responsiveness, and roadmap support quality."
        },
        {
          heading: "Quality-led engineering perception",
          body: "Client engineering leaders frequently highlight maintainable code quality and reduced release-related risk."
        },
        {
          heading: "Governance clarity",
          body: "Business and program sponsors value structured reporting and faster escalation-to-resolution turnaround."
        }
      ],
      pageFaqs: [
        {
          question: "Can we speak with existing clients before engagement?",
          answer: "Reference conversations can be arranged based on engagement stage, scope alignment, and confidentiality constraints."
        },
        {
          question: "How do you collect delivery feedback?",
          answer: "Feedback is captured through structured checkpoints, milestone retrospectives, and periodic account-level satisfaction reviews."
        }
      ]
    },
    {
      path: "/engagement-model",
      eyebrow: "How we work",
      title: "How we work and what it costs",
      subtitle: "Three ways to engage. Fixed-price scoping on all of them. Senior engineers on all of them. Direct access to the team on all of them.",
      intro: [
        "Most software firms have a confusing matrix of 'engagement models' designed to make hourly billing sound sophisticated. We have three options, and they all start the same way: a free 30-minute call, then a paid one-week scoping sprint that ends with a fixed-price quote.",
        "If you only remember one thing from this page: every option is fixed-price, every option has senior engineers, and every option includes weekly working demos with the engineers building your product."
      ],
      sections: [
        {
          heading: "Fixed-price product build (most common)",
          body: "You hand us a product idea or a brief. We do a paid 1-week scoping sprint, then quote a fixed price for the full build. We ship in 6 to 16 weeks depending on scope. You pay milestones, not hours."
        },
        {
          heading: "Dedicated retainer pod",
          body: "After launch, or for products with a longer roadmap, you can keep our team on a monthly retainer. Predictable monthly cost, weekly velocity, and you can pause or cancel with 30 days notice. Best for products with an active 6+ month roadmap."
        },
        {
          heading: "Team augmentation",
          body: "Embed one or two of our senior engineers directly into your team. Same hourly availability, your tools and rituals, weekly check-ins. Best for established product teams that need senior firepower without a hire."
        }
      ],
      process: [
        {
          title: "Week 0 — Free 30-min call",
          body: "Tell us what you want to build. We tell you honestly if we are the right team and a rough price band."
        },
        {
          title: "Week 1 — Paid scoping sprint",
          body: "One week of senior-engineer time. Architecture, milestones, and a fixed-price quote. The doc is yours either way."
        },
        {
          title: "Build phase",
          body: "Weekly working software, public progress board, direct Slack with the engineers. Ship continuously, not at the end."
        },
        {
          title: "Launch and next steps",
          body: "Deploy to your infrastructure with monitoring. Stay on retainer, switch to augmentation, or take the code and run with another team. Your call."
        }
      ],
      pageFaqs: [
        {
          question: "Why no hourly billing option?",
          answer: "Because hourly rewards us for moving slow. Fixed-price rewards us for shipping. We will not bill you by the hour, even if you ask. If we cannot scope it, we will tell you and not take the work."
        },
        {
          question: "Can I change engagement model mid-engagement?",
          answer: "Yes. Many clients start with a fixed-price build, then transition to a retainer for ongoing roadmap. Switching is straightforward at the end of any milestone."
        },
        {
          question: "Do I have to pay for the scoping sprint?",
          answer: "Yes. Scoping takes a real week of senior-engineer time and produces a real document (architecture, milestones, fixed quote) that is valuable on its own. If you do not move forward, you keep the doc."
        }
      ]
    },
    {
      path: "/pricing",
      eyebrow: "Pricing",
      title: "What things cost. Honestly.",
      subtitle: "No 'contact us for pricing.' Real ranges, real process, real numbers. The exact quote for your project lands after a one-week paid scoping sprint.",
      intro: [
        "Most software firms hide their pricing because they bill hourly and the final invoice depends on how long the team feels like taking. We do the opposite—every project starts with a paid one-week scoping sprint and ends with a fixed-price quote you see before you commit to the build.",
        "Prices below are in Indian Rupees, plus 18% GST, and reflect what we typically quote Indian SMB and startup clients. International clients can pay in USD at the equivalent rate (we mention both for context). Your number can land lower or higher depending on integrations, AI features, and design complexity. We give you the real quote after the scoping sprint."
      ],
      sections: [
        {
          heading: "MVP web app — ₹1.5L to ₹6L",
          body: "A focused single-purpose web product. Login, dashboard, the 2 or 3 core features that make the product valuable, and an admin panel. 6 to 10 weeks. Best for founders shipping v1. (~$1,800 to $7,200 for international clients.)"
        },
        {
          heading: "Full SaaS or operational platform — ₹5L to ₹18L",
          body: "Multi-tenant SaaS, customer-facing platform with billing, role-based access, integrations, and a real admin area. 10 to 16 weeks. Best for funded startups and established teams shipping a real product. (~$6,000 to $22,000.)"
        },
        {
          heading: "Travel or logistics booking platform — ₹3L to ₹12L",
          body: "Public booking site + customer accounts + admin and operations portal + payment integration. 8 to 16 weeks. We have shipped three of these (FlyJed, MM Shubh Yatra, Topsel Trucking)—the price reflects experience, not estimation. (~$3,600 to $14,500.)"
        },
        {
          heading: "Mobile app (cross-platform) — ₹2.5L to ₹9L",
          body: "React Native app for iOS and Android with backend, push notifications, auth, payments. 8 to 14 weeks. Includes Play Store and App Store submission and post-launch crash monitoring. (~$3,000 to $11,000.)"
        },
        {
          heading: "AI features for an existing product — ₹1L to ₹5L",
          body: "Intelligent search, document AI, embedded chat copilot, voice agents, or workflow automation added to a product you already have. 3 to 8 weeks. Includes OpenAI/Anthropic/Gemini API integration and prompt engineering. (~$1,200 to $6,000.)"
        },
        {
          heading: "Marketing site or listing site — ₹40k to ₹1.5L",
          body: "Fast Next.js or React site with a CMS so your marketing team can edit copy without an engineer. Lighthouse 95+, real SEO, mobile-first. 3 to 6 weeks. (~$500 to $1,800.)"
        },
        {
          heading: "Monthly retainer (post-launch) — ₹50k to ₹2.5L per month",
          body: "Keep us on after launch for new features, performance work, on-call support, and roadmap acceleration. Predictable monthly cost. Cancel with 30 days notice. (~$600 to $3,000/month.)"
        }
      ],
      serviceHighlights: [
        {
          heading: "What is always included",
          body: "Senior engineers only. Fixed price after scoping. Weekly working demos. Public progress board. Direct WhatsApp or Slack with the team. Code in your GitHub from day one. Crash monitoring and analytics at launch. A 1-hour handover call. GST invoice."
        },
        {
          heading: "What costs extra (transparent)",
          body: "Brand identity and logo work (we partner with a designer). Stock photography or media licenses. Third-party software licenses (Razorpay/Stripe transaction fees, Twilio, Sentry, hosting beyond first 3 months). On-site travel for kickoff if you want it. We quote each separately before you commit."
        },
        {
          heading: "What we will not do",
          body: "Hourly billing, even if you ask. Pad the team with juniors to lower the headline number. Take work we cannot ship well. Bypass GST or invoice through personal accounts. If we are not the right team, we will tell you and refer you to someone who is."
        }
      ],
      process: [
        {
          title: "Step 1 — Free 30-min call",
          body: "Tell us about the project. We tell you honestly if we are the right team and a rough price band based on what you describe."
        },
        {
          title: "Step 2 — Paid scoping sprint (₹25k to ₹50k)",
          body: "One week of senior-engineer time. Architecture map, milestone breakdown, fixed-price quote. You see the real number for your real project before you commit to the build. Doc is yours either way."
        },
        {
          title: "Step 3 — Build (fixed price, milestone payments)",
          body: "Working software every Friday. Public progress board. Typical payment plan: 30% on kickoff, 30% mid-milestone, 30% on staging delivery, 10% on production. The price does not change because of scope creep we caused."
        },
        {
          title: "Step 4 — Launch and decide",
          body: "Ship to production. Take the code and run with another team, or stay on a monthly retainer for new features. Your codebase, your call."
        }
      ],
      pageFaqs: [
        {
          question: "Are prices inclusive of GST?",
          answer: "No—prices listed are plus 18% GST. We are a registered Indian Private Limited and issue proper GST invoices. Indian clients can claim input tax credit on the GST portion."
        },
        {
          question: "What are the payment terms?",
          answer: "For most builds: 30% on kickoff, 30% at mid-milestone, 30% on staging delivery, 10% on production launch. Scoping sprint is 100% upfront. Retainers are billed monthly in advance."
        },
        {
          question: "Why do you publish prices when no one else does?",
          answer: "Because hiding price wastes everyone's time. Founders deserve to know roughly what something costs before they book a sales call. We would rather lose a deal at the price stage than waste two calls discovering a budget mismatch."
        },
        {
          question: "How much is the paid scoping sprint?",
          answer: "₹25,000 to ₹50,000 plus GST (or USD equivalent for international clients) depending on project complexity. It produces an architecture doc and fixed-price quote you can take to any other team if you decide we are not the right fit."
        },
        {
          question: "What if my project needs more than these ranges?",
          answer: "We will scope it honestly and quote it. Projects above ₹18L are multi-quarter builds with multiple workstreams—we either staff them properly or refer you to a larger firm."
        },
        {
          question: "Do you offer discounts?",
          answer: "No. Discount pricing rewards negotiating, not value. Our prices are calibrated to what the work actually costs to do well. If the price does not fit your budget, we will tell you what scope would fit—not lower the price arbitrarily."
        },
        {
          question: "Do you charge in INR or USD?",
          answer: "INR for Indian clients (with GST), USD for international clients. The work is the same; the currency is for your convenience. We invoice through our Indian Private Limited entity for all payments."
        },
        {
          question: "How do international clients pay?",
          answer: "We accept wire transfer (SWIFT) for international clients, with invoices issued under our Indian export-of-services GST classification. Most international payments take 2-3 business days to clear."
        },
        {
          question: "Will my project go over budget?",
          answer: "No—not because of us. Fixed-price means scope creep we cause is on us. If you change scope mid-build (new feature, new integration), we re-quote that delta clearly before any work starts."
        }
      ]
    },
    {
      path: "/privacy-policy",
      eyebrow: "Legal",
      title: "Privacy Policy",
      subtitle: "How we handle data responsibly across engagement and communication channels.",
      sections: [
        {
          heading: "Data Collection",
          body: "We collect relevant contact, project, and usage information required for communication, service delivery, and support continuity."
        },
        {
          heading: "Data Usage and Processing",
          body: "Collected information is processed for engagement operations, security monitoring, quality assurance, and contractual compliance obligations."
        },
        {
          heading: "Retention and Access Controls",
          body: "Access is role-based and time-bound, with retention controls aligned to legal, contractual, and operational requirements."
        },
        {
          heading: "Third-Party Processors",
          body: "When needed, vetted third-party platforms are used under contractual safeguards and security review standards."
        },
        {
          heading: "Data Rights and Requests",
          body: "Users can request data access, correction, or deletion through official support channels subject to applicable laws and obligations."
        },
        {
          heading: "Security Safeguards",
          body: "Technical and organizational safeguards including encryption standards, access logging, and periodic control reviews are used to protect handled information."
        },
        {
          heading: "Policy Governance",
          body: "Privacy practices are reviewed periodically to ensure alignment with legal updates, contractual commitments, and evolving security expectations."
        }
      ]
    },
    {
      path: "/terms",
      eyebrow: "Legal",
      title: "Terms of Use",
      subtitle: "General terms governing website usage and service interactions.",
      sections: [
        {
          heading: "Scope",
          body: "These terms define permissible use of the website, inquiry channels, downloadable content, and associated information resources."
        },
        {
          heading: "Responsibilities",
          body: "Both parties are expected to maintain lawful use, accurate communication, and responsible handling of shared information."
        },
        {
          heading: "Limitations",
          body: "Service information is provided on an as-available basis with legal limitations, disclaimers, and liability boundaries as permitted by law."
        },
        {
          heading: "Intellectual Property",
          body: "All trademarks, brand assets, and published content remain protected and may not be reused without written authorization."
        },
        {
          heading: "Updates to Terms",
          body: "Terms may be revised periodically; continued use of the website indicates acceptance of the latest published version."
        },
        {
          heading: "Service Information Disclaimer",
          body: "Project timelines, estimates, and scope examples presented on the site are informational and become binding only through formal agreements."
        },
        {
          heading: "Jurisdiction and Dispute Handling",
          body: "Any legal interpretation, jurisdiction scope, and dispute mechanisms are governed by the applicable contractual and legal framework."
        }
      ]
    },
    {
      path: "/web-development-services",
      eyebrow: "What we build",
      title: "Web apps that ship in weeks, not quarters",
      subtitle: "SaaS apps, internal portals, customer-facing platforms, marketing sites. Modern stack, fixed price, senior engineers, AI features built in.",
      intro: [
        "Most web builds slip because the team is junior, the requirements changed, or the agency was billing hourly. We fix all three: senior engineers only, scoped before kickoff, fixed price after scoping. The web app you wanted, on the date we agreed, for the number on the invoice.",
        "We use React, Next.js, Node, and Python by default. Postgres or Firebase for data. AWS, Vercel, or Firebase for hosting. The boring, proven stack—unless you have a specific reason for something different, in which case we will tell you the trade-offs."
      ],
      sections: [
        {
          heading: "SaaS apps and customer-facing platforms",
          body: "Multi-tenant SaaS, B2B platforms, customer portals. Auth, billing, dashboards, admin tools, and the feature work that actually closes deals."
        },
        {
          heading: "Internal portals and ops dashboards",
          body: "Admin panels, ops dashboards, broker portals, dealer portals, support tooling. Built for daily-use by your team, not for a CIO presentation."
        },
        {
          heading: "Marketing sites that convert",
          body: "Fast, SEO-ready Next.js sites with CMS so your marketing team can edit copy without calling us. Lighthouse 95+ out of the box."
        },
        {
          heading: "Legacy app rebuilds",
          body: "Take a slow PHP or jQuery app and rebuild it as a modern React/Next product. We migrate users, data, and integrations carefully and incrementally—your existing users do not notice the switch."
        },
        {
          heading: "Integration and API work",
          body: "Connect to your CRM, payment processor, identity provider, third-party APIs, or that one important system written in 2014. Clean integrations, tested edge cases."
        },
        {
          heading: "AI features built in",
          body: "Search that understands intent, document AI for back-office, embedded chat copilots, automation workflows. We design AI into the app from day one."
        }
      ],
      serviceHighlights: [
        {
          heading: "SaaS platform engineering",
          body: "Multi-tenant architecture, role-based access, subscription billing (Stripe/Razorpay), and analytics-rich dashboards."
        },
        {
          heading: "Customer and broker portals",
          body: "Secure portals for customers, partners, brokers, or field staff. Document handling, workflow automation, and clear permissions."
        },
        {
          heading: "Fast marketing sites",
          body: "Next.js + a headless CMS. Lighthouse 95+, real SEO, edits by your marketing team without an engineer."
        },
        {
          heading: "Integration-heavy platforms",
          body: "API-first systems integrated with CRM, ERP, payment, identity, and third-party data providers."
        }
      ],
      differentiators: [
        {
          title: "Fixed scope, fixed price",
          body: "One-week paid scoping sprint, then a fixed price for the full build. No hourly billing, no scope creep invoices."
        },
        {
          title: "Senior engineers only",
          body: "No juniors hidden in the team. You work directly with the people writing your code."
        },
        {
          title: "Working software every Friday",
          body: "You see a real deployed build every week. Public progress board, direct Slack with the team."
        },
        {
          title: "You own the code",
          body: "Repos, infrastructure, and IP are yours from day one. Take it to any other team at any point."
        }
      ],
      process: [
        {
          title: "Week 0 — Free 30-min call",
          body: "Tell us what you want to build. We will tell you honestly if we are the right team and a rough price band."
        },
        {
          title: "Week 1 — Paid scoping sprint",
          body: "One week of senior-engineer time. Architecture map, milestone breakdown, fixed price quote. Doc is yours either way."
        },
        {
          title: "Weeks 2 to N — Build",
          body: "Working software every Friday. Public progress board. Direct Slack with engineers. Ship continuously, not at the end."
        },
        {
          title: "Launch and optional retainer",
          body: "Deploy to your infrastructure, set up monitoring and alerts, and either hand off or stay on for new features."
        }
      ],
      technologyGroups: [
        {
          label: "Frontend",
          items: ["React", "Next.js", "Vue", "TypeScript", "HTML5", "CSS3"]
        },
        {
          label: "Backend",
          items: ["Node.js", "Python", "Java", ".NET", "Go", "PHP"]
        },
        {
          label: "Data and Storage",
          items: ["PostgreSQL", "MySQL", "MongoDB", "Redis", "Elasticsearch"]
        },
        {
          label: "Cloud and DevOps",
          items: ["AWS", "Azure", "GCP", "Docker", "Kubernetes", "Terraform"]
        }
      ],
      pageFaqs: [
        {
          question: "How long does a typical web app take to ship?",
          answer: "Most MVPs ship in 6 to 10 weeks from the scoping call. Larger platforms run 3 to 6 months. You see working software every Friday."
        },
        {
          question: "Can you modernize our current site without breaking it?",
          answer: "Yes. We use phased migration with parallel environments. Users on the old site keep using it until the new one is ready, then we cut over."
        },
        {
          question: "Do you handle SEO and Core Web Vitals?",
          answer: "Yes. Technical SEO, page speed, structured metadata, sitemap generation, and Core Web Vitals are part of every build by default."
        },
        {
          question: "Can my team take over after launch?",
          answer: "Always. We hand over the code, infrastructure, and a written handover doc plus a 1-hour walkthrough call."
        },
        {
          question: "What does a web app typically cost?",
          answer: "MVPs land between ₹1.5L and ₹12L (plus GST). Larger platforms with custom integrations run higher. Fixed quote after a one-week paid scoping sprint."
        }
      ]
    },
    {
      path: "/mobile-app-development",
      eyebrow: "What we build",
      title: "Mobile apps that work on actual phones",
      subtitle: "iOS, Android, and cross-platform apps. Built to work on Android 8 with two bars of signal, not just on a flagship iPhone in a demo room.",
      intro: [
        "Most agency-built mobile apps fail one of three real-world tests: they crash on older Android phones, they break on bad connections, or they need an update every two weeks to fix something basic. We build the opposite—tested across actual devices, designed for offline-first behavior where it matters, and shipped with crash reporting on day one.",
        "We default to React Native because it ships faster and runs well on both platforms. We go native (Swift, Kotlin) when the app needs camera, audio, ML, or background work that React Native cannot do well. We will tell you which one you need."
      ],
      sections: [
        {
          heading: "Cross-platform apps in React Native",
          body: "One codebase, both stores. 80% of mobile builds we do are React Native because it ships faster and the UX is indistinguishable from native for most use cases."
        },
        {
          heading: "Native iOS and Android when needed",
          body: "Swift and Kotlin for apps with heavy camera, audio, ML, background processing, or platform-specific hardware needs. We will tell you honestly when native is actually worth the cost."
        },
        {
          heading: "Backend, APIs, and auth",
          body: "Most mobile apps need a backend. We build it as part of the same engagement—Node or Python APIs, Firebase or Postgres, plus auth, push notifications, and analytics integrated."
        },
        {
          heading: "Store submission and release management",
          body: "We handle App Store and Play Store submissions, metadata, screenshots, review responses, and version rollouts. Most agencies leave this to you; we do not."
        }
      ],
      serviceHighlights: [
        {
          heading: "React Native cross-platform",
          body: "Faster shipping, one team, one codebase. Real native feel through proper navigation, gestures, and platform-specific behavior."
        },
        {
          heading: "Native iOS and Android",
          body: "Swift and Kotlin when the use case demands it. Camera, audio, ML on-device, background tasks, advanced hardware."
        },
        {
          heading: "Backend and integrations",
          body: "Full backend, push notifications, analytics, deep linking, payment integration, social auth—built as part of the app."
        },
        {
          heading: "Store launch and updates",
          body: "Submission, metadata, screenshots, review responses, and OTA updates via CodePush or EAS Update."
        }
      ],
      process: [
        {
          title: "Week 0 — Free call",
          body: "Tell us about the app. We will recommend native vs cross-platform honestly and a rough price band."
        },
        {
          title: "Week 1 — Paid scoping sprint",
          body: "Architecture map, screen list, milestone plan, and a fixed-price quote for the full build."
        },
        {
          title: "Weeks 2 to N — Build",
          body: "Working app on TestFlight or internal track every Friday. You and your team can install and use it through the build."
        },
        {
          title: "Launch — Store submission and rollout",
          body: "We submit, handle review responses, and ship. Crash monitoring and analytics live from day one."
        }
      ],
      pageFaqs: [
        {
          question: "Should we go native or cross-platform?",
          answer: "Default to React Native unless you need heavy camera/audio/ML on-device or background work. We will tell you honestly which one fits—and if you do not need both platforms, we will say that too."
        },
        {
          question: "Can you take over an existing app?",
          answer: "Yes. We start with a 1-week audit of the codebase, identify the must-fix issues, and quote stabilization plus next features as one engagement."
        },
        {
          question: "Do you handle App Store and Play Store submission?",
          answer: "Yes. End to end. Including metadata, screenshots, review responses, and OTA updates after launch."
        },
        {
          question: "What does a mobile app typically cost?",
          answer: "Most cross-platform MVPs land between ₹2.5L and ₹9L (plus GST). Native dual-platform builds run higher. Fixed quote after a one-week paid scoping sprint."
        }
      ]
    },
    {
      path: "/ui-ux-design-services",
      eyebrow: "Design",
      title: "Product design that ships",
      subtitle: "We do product UX and UI design as part of every build. Wireframes, flows, design systems, and high-fidelity screens—then the engineers who designed them build them. No handoff drama.",
      intro: [
        "Most agencies hand design files to a separate engineering team that 'interprets' them. Stuff breaks in translation, deadlines slip, and the final product looks 80% like the mockup. We design and build in the same room, so what is in Figma is what ships.",
        "We do not do logo or brand identity work. For that, we partner with designers we trust. We do product design—the screens, flows, and design systems that make your software actually usable."
      ],
      sections: [
        {
          heading: "Product flow and wireframing",
          body: "Map the screens, the navigation, the empty states, and the error states before any pixel-pushing. Saves weeks of rework later."
        },
        {
          heading: "High-fidelity UI design",
          body: "Pixel-perfect screens in Figma, with proper states (hover, loading, error, success). Built into a real design system so it scales as features get added."
        },
        {
          heading: "Design systems and component libraries",
          body: "Reusable Figma components mirrored by code components. Adding a new feature later does not mean redesigning anything that exists."
        },
        {
          heading: "Mobile-first responsive design",
          body: "Designed for the phone first, then scaled up to tablet and desktop. Because most of your users will be on mobile, even if your dashboard looks fine on a 27-inch monitor."
        }
      ],
      pageFaqs: [
        {
          question: "Can you redesign just a flow without rebuilding everything?",
          answer: "Yes. Onboarding redesign, checkout redesign, dashboard redesign—we scope and ship targeted improvements without forcing a full rebuild."
        },
        {
          question: "Do you do brand identity, logo, or marketing visuals?",
          answer: "No—we do product design only. For brand work we partner with designers we trust and can introduce you."
        }
      ]
    },
    {
      path: "/devops-services",
      eyebrow: "Infrastructure",
      title: "DevOps that works on a small team",
      subtitle: "CI/CD pipelines, monitoring, infrastructure as code—done right for teams that do not have a 10-person DevOps department. Set up once, runs forever.",
      intro: [
        "Most DevOps content online assumes you have a platform team. Most teams do not—they have one engineer wearing four hats. We set up CI/CD and monitoring the way a small team actually uses it: simple, well-documented, and impossible to break by accident.",
        "We use GitHub Actions or GitLab CI for pipelines, Terraform or Pulumi for IaC, Sentry for errors, and one of Datadog/Grafana/Better Stack for monitoring. Boring, proven tooling that small teams can actually maintain."
      ],
      sections: [
        {
          heading: "CI/CD pipelines that actually deploy",
          body: "GitHub Actions or GitLab CI workflows that build, test, and deploy on every push. Preview environments for every PR. Rollback in one click."
        },
        {
          heading: "Infrastructure as code (Terraform / Pulumi)",
          body: "Your AWS or GCP setup in a repo, reviewable, reproducible. No more 'we think someone clicked something in the console six months ago.'"
        },
        {
          heading: "Monitoring, errors, and on-call",
          body: "Sentry, log aggregation, uptime checks, and PagerDuty-style alerts. You hear about problems before your users do."
        },
        {
          heading: "Documentation that future-you will read",
          body: "Every setup we ship comes with a README that explains how to deploy, how to roll back, and what to do when X breaks. Written for a tired engineer at 2am, not for an audit."
        }
      ],
      pageFaqs: [
        {
          question: "Can you improve our existing pipeline instead of rebuilding it?",
          answer: "Yes. We audit what you have, fix the worst pain points (slow builds, flaky tests, manual deploy steps), and document what we changed. Most teams see big wins in 1-2 weeks."
        },
        {
          question: "Do you work with self-hosted Kubernetes?",
          answer: "Yes, but we will tell you honestly if you would be happier on a managed platform like Vercel, Railway, or Fly.io. Kubernetes is the right answer less often than people think."
        }
      ]
    },
    {
      path: "/cloud-services",
      eyebrow: "Infrastructure",
      title: "Get your product on a real cloud",
      subtitle: "Move off shared hosting, a random VPS, or an aging server. AWS, GCP, Vercel, or Firebase—whichever fits your product. Set up properly, not over-engineered.",
      intro: [
        "Most small teams either over-engineer cloud (Kubernetes for a SaaS that has 200 users) or under-engineer it (an EC2 instance someone SSH'd into in 2021 that nobody can touch). We pick the simplest cloud setup that actually fits your product, set it up properly, and document it.",
        "Default recommendation for most products: Vercel + a managed Postgres or Firebase. For products that grow past that, AWS with sensible defaults. Kubernetes only when you actually need it, which is rarely."
      ],
      sections: [
        {
          heading: "Pick the right cloud (honest recommendation)",
          body: "Vercel for Next.js apps with light backend. Firebase for fast prototypes and most B2C. AWS for established platforms with custom needs. GCP when you need BigQuery or specific Google services. We tell you what fits and what does not."
        },
        {
          heading: "Migration from old hosting",
          body: "Move from shared hosting, a single VPS, or an aging server to a modern cloud setup. Zero or near-zero downtime through parallel deployment and DNS cutover."
        },
        {
          heading: "Database setup that survives growth",
          body: "Managed Postgres (Supabase, Neon, RDS) or Firebase Firestore with proper backups, read replicas where needed, and a migration plan that does not require taking the product offline."
        },
        {
          heading: "Cost cleanup",
          body: "Most teams overpay 30-60% on cloud. We audit your bill, kill orphaned resources, right-size what is running, and move what should be serverless to serverless."
        }
      ],
      technologyGroups: [
        {
          label: "Cloud Platforms",
          items: ["AWS", "Microsoft Azure", "Google Cloud Platform", "Oracle Cloud"]
        },
        {
          label: "Container and Orchestration",
          items: ["Docker", "Kubernetes", "OpenShift", "Helm"]
        },
        {
          label: "Infrastructure and Automation",
          items: ["Terraform", "Ansible", "CloudFormation", "GitHub Actions", "Jenkins"]
        },
        {
          label: "Monitoring and Reliability",
          items: ["Prometheus", "Grafana", "Datadog", "Elastic Stack", "Cloud-native observability"]
        }
      ],
      pageFaqs: [
        {
          question: "Will my product be down during the migration?",
          answer: "No—not if we do it right. We use parallel environments and DNS cutover so the switch is seconds long. Database migrations need a brief read-only window we agree on in advance."
        },
        {
          question: "Should we use Kubernetes?",
          answer: "Probably not. Kubernetes is the right answer when you have 50+ services or specific orchestration needs. Most products under 50k DAU are better off on Vercel, Railway, Fly.io, or a few EC2 instances. We will tell you honestly which one fits."
        },
        {
          question: "What does cloud setup or migration cost?",
          answer: "Most setups land between ₹60,000 and ₹3.5L (plus GST) depending on the complexity of what we are migrating. We give you a fixed price after a 1-week audit."
        }
      ]
    },
    {
      path: "/healthcare-software-solutions",
      eyebrow: "Industry expertise",
      title: "Healthcare apps, portals, and patient tools",
      subtitle: "We build appointment booking, patient portals, intake forms, and clinic ops dashboards. Not EHR replacements—the software that makes the existing one usable.",
      intro: [
        "Healthcare software does not need to be 'transformative' to be useful. Most clinics, labs, and diagnostic chains we talk to need three things: a patient-facing booking and intake flow that does not require an app download, an admin dashboard that handles the day-to-day, and AI features that cut paperwork.",
        "That is what we build. We are not an EHR vendor and we do not pretend to be. We build the products around the EHR—or, for smaller practices, the lightweight system that replaces a clipboard."
      ],
      sections: [
        {
          heading: "Patient booking and intake",
          body: "Online appointment booking, digital intake forms, prescription requests, lab report access, and follow-up reminders. Works in a browser—no app install required."
        },
        {
          heading: "Clinic and practice ops dashboards",
          body: "Scheduling, queue management, billing helpers, staff rotations, and patient comms. Designed for front-desk staff using a $200 Windows machine, not for a CIO presentation."
        },
        {
          heading: "Document AI for healthcare",
          body: "Pull data from prescriptions, lab reports, and insurance documents into structured fields so staff spend time with patients instead of typing."
        },
        {
          heading: "Integrations into existing systems",
          body: "We connect into your EHR, lab system, pharmacy ordering, billing, and payment processors through clean integration layers. Your existing stack stays; we make it work better."
        }
      ],
      serviceHighlights: [
        {
          heading: "Online booking and intake",
          body: "Patient-side flows that actually convert—a one-page booking, a clear intake form, and an SMS confirmation that arrives."
        },
        {
          heading: "Clinic admin portal",
          body: "A real ops dashboard for front-desk and clinic staff, not a generic CRM that gets ignored."
        },
        {
          heading: "Lab and report delivery",
          body: "Secure delivery of lab reports and prescriptions to patients, with read receipts and follow-up reminders."
        }
      ],
      pageFaqs: [
        {
          question: "Do you handle HIPAA / data privacy compliance?",
          answer: "We build to your compliance team's standards—encryption at rest and in transit, role-based access, audit logs, and data retention controls. We are not a HIPAA-certified entity; you are. We build the software to support your certification."
        },
        {
          question: "Do you do telehealth?",
          answer: "We can integrate Zoom, Twilio Video, or a similar third-party video stack into your booking flow. We do not build a video infrastructure from scratch—you do not need that."
        },
        {
          question: "What does a typical healthcare engagement cost?",
          answer: "A clinic booking + admin portal v1 typically lands between ₹2L and ₹7L (plus GST). Larger multi-location systems run higher. Fixed quote after a one-week paid scoping sprint."
        }
      ]
    },
    {
      path: "/fintech-banking-solutions",
      eyebrow: "Industry expertise",
      title: "Fintech apps, portals, and ops tools",
      subtitle: "We build the customer-facing apps and internal ops tooling that sit on top of your core financial infrastructure. Modern stack, fixed price, AI features built in.",
      intro: [
        "We are not a core banking vendor. We build the products around a core—lending portals, KYC and onboarding flows, broker and agent dashboards, repayment apps, recovery ops tools, payment status pages, customer self-service portals, and the admin tooling your ops team actually uses.",
        "Most fintech firms we talk to do not need another 'platform.' They need a clean React app on top of their existing APIs, an operations portal that does not crash on a slow connection, and AI features that actually save staff time."
      ],
      sections: [
        {
          heading: "Customer-facing fintech apps",
          body: "Lending application flows, KYC and document upload journeys, repayment portals, account dashboards, and customer self-service. Built mobile-first so they work on Android 8 with patchy data."
        },
        {
          heading: "Operations and broker portals",
          body: "Internal portals for credit officers, recovery agents, brokers, and ops staff. Built for the people who use them every day, not for executives looking at dashboards once a quarter."
        },
        {
          heading: "AI features that actually save work",
          body: "Document understanding for KYC and underwriting, intelligent search across customer records, automated drafting of letters and statements, voice and chat copilots for support staff."
        },
        {
          heading: "Integrations into your stack",
          body: "We connect into core banking systems, payment processors, KYC providers, credit bureaus, and bureau APIs through clean integration layers. We do not replace your core—we make it usable."
        }
      ],
      serviceHighlights: [
        {
          heading: "KYC and onboarding journeys",
          body: "Friction-tested onboarding with document upload, verification status, and clear customer messaging when something fails."
        },
        {
          heading: "Lending and recovery portals",
          body: "Application, decisioning, disbursement, and recovery workflows for the staff doing the work day-in day-out."
        },
        {
          heading: "Document AI for underwriting",
          body: "Pull structured data out of bank statements, payslips, tax returns, and ID documents so underwriters skip the typing."
        }
      ],
      pageFaqs: [
        {
          question: "Are you a regulated entity?",
          answer: "No. We are an engineering partner. You hold the regulatory licenses and core relationships; we build the software around them."
        },
        {
          question: "Can you handle compliance and audit requirements?",
          answer: "Yes—we build with audit logs, role-based access, encryption at rest, and clear data retention controls. We work to your compliance team's standards rather than imposing our own."
        },
        {
          question: "What does a typical fintech engagement cost?",
          answer: "Most lending portal or onboarding-flow builds land between ₹3L and ₹10L (plus GST) for a v1 launch. We give you a fixed quote after a one-week paid scoping sprint."
        }
      ]
    },
    {
      path: "/real-estate-digital-solutions",
      eyebrow: "Industry expertise",
      title: "Real estate listing sites, broker portals, and lead tools",
      subtitle: "We build the listing sites, agent portals, and lead-management tools that actually get used by brokers and ops staff. Mobile-first, integration-friendly, AI-enhanced.",
      intro: [
        "Real estate software has a usability problem. Most platforms are built for a CIO to demo to a board, not for a 24-year-old field agent on an Android phone with two bars of signal. We build the opposite—simple, fast tools that get used.",
        "Whether you are a residential brokerage launching a listing portal, a developer building a project showcase site, or a multi-branch operation that needs a central CRM—we scope it, price it, and ship it in 6 to 12 weeks."
      ],
      sections: [
        {
          heading: "Listing and project showcase sites",
          body: "Public-facing sites with property search, media galleries, virtual tours, enquiry forms, and SEO-ready content. Built fast, ranks well, and converts."
        },
        {
          heading: "Broker and agent portals",
          body: "Internal portals for listing management, lead routing, viewing scheduling, and offer tracking. Mobile-first because that is where the agent actually works."
        },
        {
          heading: "Lead management and follow-up automation",
          body: "Auto-routing of enquiries by location and price band, follow-up reminders, WhatsApp and SMS integration, and clear pipeline visibility for sales managers."
        },
        {
          heading: "AI features that actually convert",
          body: "Property matching from a natural-language search query, automated listing description generation, image quality scoring, and chat-based lead qualification."
        }
      ],
      serviceHighlights: [
        {
          heading: "Listing site + agent portal as one product",
          body: "Public site and internal portal share the same listing data, the same uploads, the same updates. No double entry."
        },
        {
          heading: "WhatsApp-first lead capture",
          body: "Enquiries route into WhatsApp, where the agent actually replies. Logged back into the portal so nothing slips."
        },
        {
          heading: "Multi-branch and role controls",
          body: "Branch-level visibility, role-based permissions, and centralized reporting for principal brokers managing teams."
        }
      ],
      pageFaqs: [
        {
          question: "Can you handle multi-language and multi-currency listings?",
          answer: "Yes. We have built multi-language flows before and can support Hindi, English, Arabic, and other scripts. Currency conversion and locale-aware pricing are straightforward."
        },
        {
          question: "Do you integrate with MagicBricks, 99acres, or Housing.com?",
          answer: "Yes—we can sync listings out to major portals and route enquiries back through their APIs. Specifics depend on what they expose; we figure that out in the scoping sprint."
        },
        {
          question: "What does a real estate platform typically cost?",
          answer: "A listing site + agent portal v1 lands between ₹2L and ₹8L (plus GST). Larger multi-branch systems with custom CRM run higher. Fixed quote after a one-week paid scoping sprint."
        }
      ]
    },
    {
      path: "/travel-logistics-solutions",
      eyebrow: "Industry expertise",
      title: "We build travel, logistics, and operational platforms",
      subtitle: "Three of our four shipped products run in travel, pilgrimage, or trucking operations. If you are building one of these, we know the problems before you describe them.",
      intro: [
        "Travel and logistics products fail in three predictable ways: the booking flow leaks customers, the operations portal is too slow for the staff that actually uses it, and the partner integrations break silently. We have built and shipped four products in this space and have a strong opinion about all three.",
        "Whether you are a travel operator launching a new booking site, a logistics company digitizing dispatch, or a multi-location operation that needs a central control panel—we can scope it, price it, and ship it in 8 to 16 weeks."
      ],
      sections: [
        {
          heading: "Travel and pilgrimage booking platforms",
          body: "End-to-end booking sites with package discovery, search, pricing rules, payment integration, customer accounts, and admin tools. Built two of these already (FlyJed, MM Shubh Yatra)—we know the booking-flow conversion killers and how to avoid them."
        },
        {
          heading: "Logistics and trucking portals",
          body: "Public sites + internal operations portals for trucking, fleet, and dispatch. Service workflows, location visibility, and staff-facing tools that work on slow devices in the field. Built and shipped (Topsel Trucking)."
        },
        {
          heading: "Inventory and stock tracking systems",
          body: "Internal portals for inventory visibility, stock allocation, and movement tracking. Built for daily operational use by non-technical staff, not for executives looking at dashboards. Built and shipped (Toyota Stock Track)."
        },
        {
          heading: "Customer support and operational ops tooling",
          body: "Ticketing, escalation, and customer communication tooling integrated into the booking or logistics flow—so customers do not bounce when something goes wrong."
        }
      ],
      serviceHighlights: [
        {
          heading: "Real shipped proof",
          body: "Four production products in this space. Click any of them—FlyJed, MM Shubh Yatra, Topsel Trucking, Toyota Stock Track—and see the work."
        },
        {
          heading: "8–16 week delivery",
          body: "Most travel/logistics products in this category ship in 8 to 16 weeks from scoping call. Faster than agencies, with senior engineers from day one."
        },
        {
          heading: "AI features designed in",
          body: "Intelligent search, automated customer support, route optimization, demand prediction—we build these in from the architecture, not bolted on later."
        }
      ],
      pageFaqs: [
        {
          question: "Can you also handle the website + admin portal + mobile app together?",
          answer: "Yes—we do the full stack. Public booking site, customer mobile app, internal operations portal, and admin tools as one coherent system. That is the only way the integrations actually work."
        },
        {
          question: "What does a travel booking platform typically cost?",
          answer: "Most full booking platforms (web + admin + payment + accounts) land between ₹3L and ₹12L (plus GST) depending on integrations. We give you a fixed quote after a one-week paid scoping sprint."
        },
        {
          question: "Can you talk to one of your travel clients?",
          answer: "Yes. After the first call we will introduce you to FlyJed or MM Shubh Yatra for a direct 15-minute reference chat. No script, no sales role."
        }
      ]
    },
    {
      path: "/public-sector-it-solutions",
      eyebrow: "Industry",
      title: "Public sector IT solutions",
      subtitle: "Build resilient digital public services with accessibility, governance, and transparency at the core.",
      intro: [
        "Public service systems must be inclusive, resilient, and auditable. We build digital public platforms that improve citizen access while supporting policy-driven governance and operational continuity.",
        "From service request workflows to process digitization and accessibility standards, our approach ensures technology modernization aligns with institutional accountability."
      ],
      sections: [
        {
          heading: "Citizen Service Platforms",
          body: "Digital service portals are designed for request management, application workflows, and communication transparency for public users." 
        },
        {
          heading: "Governance and Process Digitization",
          body: "Workflow modernization reduces administrative overhead while improving compliance tracking and decision accountability." 
        },
        {
          heading: "Security and Accessibility Standards",
          body: "Public-sector systems are delivered with strong security controls and accessibility-first UX to ensure inclusive and compliant experiences." 
        },
        {
          heading: "Program Delivery and Continuity",
          body: "Structured rollout models, stakeholder reporting, and continuity planning keep critical services stable during transformation programs." 
        }
      ],
      serviceHighlights: [
        {
          heading: "Citizen service portals",
          body: "Digital channels for requests, applications, status tracking, and communication updates across public programs."
        },
        {
          heading: "Workflow digitization",
          body: "Modernized internal processes for approvals, records handling, and case-level accountability."
        },
        {
          heading: "Accessibility and compliance controls",
          body: "Inclusive UX and governance-ready controls aligned with public-sector standards and reporting requirements."
        }
      ],
      pageFaqs: [
        {
          question: "Can your solutions work across multiple departments?",
          answer: "Yes. We support shared platform models with configurable workflows and permission controls for department-specific operations."
        },
        {
          question: "How do you handle accessibility in public-facing systems?",
          answer: "Accessibility criteria are integrated into UX, engineering, and QA checkpoints to ensure inclusive and compliant delivery."
        }
      ]
    },
    {
      path: "/sitemap",
      eyebrow: "Navigation",
      title: "Sitemap",
      subtitle: "A structured index of core pages, services, and support resources.",
      sections: [
        {
          heading: "Company",
          body: "About Us, Leadership, Careers, Insights, Partners, Corporate Social Responsibility, and Contact."
        },
        {
          heading: "Services",
          body: "Services, Product Engineering, Cloud Modernization, Data and AI, Managed Support, Cybersecurity, and Compliance and Risk."
        },
        {
          heading: "Support and Legal",
          body: "FAQ, Engagement Model, Client Reviews, Privacy Policy, Terms of Use, and Sitemap."
        },
        {
          heading: "Industry and Proof",
          body: "Industries and Case Studies pages covering sector context and delivery outcomes."
        },
        {
          heading: "Consultation Journey",
          body: "Contact page and consultation pathways for roadmap discovery, architecture planning, and engagement discussion."
        }
      ]
    }
  ],
  /* Direct-contact channels. WhatsApp is the primary one — Akash answers it
     personally. The number is stored digits-only in whatsapp because wa.me
     requires the country code with no +, spaces or dashes; phoneDisplay is what
     humans read and phoneHref is the tel: value.
     TODO(content): if the number changes, change it HERE only — the header,
     the sticky mobile bar, the contact page and the footer all read from this. */
  contact: {
    whatsapp: "917348370221",
    phoneDisplay: "+91 73483 70221",
    phoneHref: "+917348370221",
    email: "info@appinfigo.com",
    /* Pre-filled WhatsApp openers, so the first message is never a blank box.
       Keep them short — they show up in the user's compose field. */
    messages: {
      default: "Hi Appinfigo, I'd like to talk about a project.",
      quote: "Hi Appinfigo, I'd like a fixed-price quote. Here's what I want to build:",
      call: "Hi Appinfigo, I'd like to book a 30-minute call.",
      zeta: "Hi Appinfigo, I'd like to know more about Zeta."
    },
    responseNote: "Team usually replies within an hour",
    hoursNote: "Mon-Sat, 10am-8pm IST"
  },

  /* Appinfigo's OWN product, as distinct from client work in caseStudies.
     Live on Google Play since September 2026 (verified against the listing).
     The App Store build is NOT live: appStoreUrl stays "" and "App Store" stays
     under comingTo until it is actually listed. Never render a store link whose
     URL is empty. */
  ownProduct: {
    name: "Zeta",
    byline: "by Appinfigo",
    tagline: "Quick commerce, built in-house",
    status: "live",
    launchLabel: "Live on Google Play",
    /* Platforms still to come. Move a name out of here when its URL is filled. */
    comingTo: ["App Store"],
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.appinfigo.coreappcommerce",
    /* TODO(content): fill on App Store launch, and remove "App Store" from comingTo. */
    appStoreUrl: "",
    logo: "/images/products/zeta-logo.png",
    logoOnDark: "/images/products/zeta-logo-white.png",
    summary:
      "A hyperlocal dark-store grocery app, built to put groceries and daily essentials at your door in 10 to 15 minutes. We designed and engineered the whole thing in-house — four apps and the backend behind them — and we run it ourselves.",
    why:
      "We fund and run Zeta ourselves, on the same stack and the same standards we hold client work to. It is why we can talk about dark-store logistics, rider routing and payment reliability as problems we have actually had to solve — not as slides.",
    firstCity: "Civil Lines, Prayagraj",
    /* Four separate apps, all built by the same senior team. */
    apps: [
      { name: "Customer app", detail: "Browse, cart, pay, live order tracking", icon: "mobile" },
      { name: "Picker app", detail: "In-store pick and pack for dark-store staff", icon: "layers" },
      { name: "Rider app", detail: "Delivery routing and proof of drop-off", icon: "logistics" },
      { name: "Ops dashboard", detail: "Catalogue, inventory, orders and staff", icon: "analytics" }
    ],
    /* Real stack, from the Zeta repo — nothing aspirational in this list. */
    stack: ["React Native", "Expo", "Next.js", "TypeScript", "Firebase", "Cloud Functions", "Firestore", "Razorpay"],
    ctaLabel: "Talk to us about Zeta",

    /* ---- Marketing page at /zeta -------------------------------------
       Copy here is Appinfigo's own launch copy, supplied verbatim. Anything
       that states a price, a delivery time or an offer lives in the ARTWORK,
       not in this text — the creatives are dated assets that can be swapped,
       whereas text here would silently go stale. Do not move a promotional
       number out of an image and into a string. */
    promo: {
      headline: "Daily needs, better prices, faster delivery.",
      sub:
        "From snacks and groceries to personal care and home essentials — get it all on Zeta. Download now and start shopping.",
      downloadLabel: "Download Zeta",
      qr: "/images/products/zeta-play-qr.svg",
      qrCaption: "Scan to download",

      /* What the app does, stated plainly. No prices, no timings, no offers —
         those change and would date the page. */
      highlights: [
        { title: "Shop by category", detail: "Snacks, bakery, everyday grocery, tea and coffee, personal care and more.", icon: "layers" },
        { title: "Simple cart", detail: "Add, review and adjust quantities, then a short path to checkout.", icon: "retail" },
        { title: "Live order tracking", detail: "Follow your order from confirmed, to packed nearby, to out for delivery.", icon: "logistics" },
        { title: "Secure payments", detail: "Card and UPI handled inside the payment provider's own flow.", icon: "security" }
      ],

      /* The launch creatives, optimised from the originals (16.9MB -> 2.4MB).
         The flagship one leads the page; the rest fill the gallery.

         DELIBERATELY CURATED: ten creatives were supplied, but four are near
         duplicate layouts of another (two more "Everything you need" and one
         more "Top deals"). Showing all ten would read as padding rather than a
         product tour, so only the six distinct messages are listed. The
         duplicates are still in public/images/zeta/ — swap a filename below to
         use one instead:
           zeta-everything-alt.jpg, zeta-everything-tall.jpg,
           zeta-everything-tall-2.jpg, zeta-top-deals-tall.jpg

         A file that is missing simply does not render — the gallery never shows
         a broken frame. */
      hero: {
        file: "zeta-everything.jpg",
        alt:
          "Zeta app home screen beside a Zeta tote bag of fresh groceries — everything you need, delivered fast"
      },
      gallery: [
        { file: "zeta-categories.jpg", alt: "Shop by category in Zeta: snacks, bakery, everyday grocery, tea and coffee, personal care" },
        { file: "zeta-top-deals.jpg", alt: "Top deals in Zeta on everyday snacks and household brands" },
        { file: "zeta-cart.jpg", alt: "The Zeta cart: add, review and adjust items, then checkout" },
        { file: "zeta-delivery.jpg", alt: "A Zeta rider delivering an order, with live order tracking from confirmed to out for delivery" },
        { file: "zeta-essentials.jpg", alt: "A Zeta trolley of daily essentials — fruit, vegetables, bread, milk and snacks" }
      ]
    }
  },

  ui: {
    headerButton: "Talk to Expert",
    quickActions: [
      "Book Consultation",
      "View Case Studies",
      "Explore Services",
      "Talk to Delivery Team",
      "Download Company Profile"
    ]
  }
};
